/* eslint-disable no-prototype-builtins */
import React, {
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { t } from 'i18next'
import { CircularProgress } from '@mui/material'
import { Row, Col } from 'react-bootstrap'
import { VAContext } from 'Context/VAContext'
import { fetchSearchCustomers } from 'redux/actions/clientsActions'
import NewCustomerMap from 'Components/NewCustomerMap'
import { DisplayCustomerGroup } from '../DisplayCustomerGroup'
import {
  VAPermissions,
  customerTypeAllowedRoles,
  customerTypeId,
  documentTypeIds,
} from 'Utils/constants'
import { useValidateRoleAndPermission } from 'Hooks/useValidateRoleAndPermission'
import PhoneNumberField from '../PhoneNumberField/PhoneNumberField'
import { PriceList } from '../PriceList'
import {
  DocumentTypeTooltipLegal,
  EmailTooltip,
  PhoneTypeTooltip,
} from '../ClientTooltips'
import useValidatePermission from 'Hooks/useValidatePermission'
import DuplicateRucModal from '../DuplicateRucModal'

export const TabLegal = ({
  props: {
    formikLegal,
    documentType,
    tipoTelefono,
    setValueState,
    valueState,
    loadingSave,
    setHasAddress,
    hasAddress,
    setCountryCode,
    setProvinceCode,
    setDistrictCode,
    setTownshipCode,
    street,
    setStreet,
    name,
    setName,
  },
}) => {
  const { clientSelected } = useContext(VAContext)
  const dispatch = useDispatch()
  const companyId = useSelector(state => state.company.CompanyId)

  // Estados para validación de RUC duplicado
  const [isCheckingRuc, setIsCheckingRuc] = useState(false)
  const [hasDuplicateRuc, setHasDuplicateRuc] = useState(false)
  const lastCheckedRuc = useRef(null)
  const isFirstDocTypeRender = useRef(true)

  // Estados locales para el modal de RUC duplicado
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [duplicateClients, setDuplicateClients] = useState([])

  // Handlers para el modal de RUC duplicado
  const handleOpenModal = useCallback(clients => {
    setDuplicateClients(clients)
    setIsModalOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
    setDuplicateClients([])

    // Si hay duplicado y el usuario cerró sin ingresar aviso operacional, mostrar error inmediatamente
    if (hasDuplicateRuc && !formikLegal.values.OperationNoticeNumber?.trim()) {
      formikLegal.setFieldTouched('OperationNoticeNumber', true, false)
      formikLegal.setFieldError(
        'OperationNoticeNumber',
        t('CREATE_CLIENT_T.OPERATIONAL_NOTICE_NUMBER_REQUIRED')
      )
    }
  }, [hasDuplicateRuc, formikLegal])

  const handleContinueModal = useCallback(
    ({ operationalNumber }) => {
      formikLegal.setFieldValue('OperationNoticeNumber', operationalNumber)
      handleCloseModal()
    },
    [formikLegal, handleCloseModal]
  )

  const canUserAssignPriceLists = useValidatePermission(
    VAPermissions.canAssignPriceLists
  )
  const canUseCustomerType = useValidateRoleAndPermission(
    Object.values(customerTypeAllowedRoles),
    VAPermissions.customerType
  )

  // IDs de tipos de documento
  const RUC_DOCUMENT_TYPE_ID = documentTypeIds.legal.RUC
  const CEDULA_DOCUMENT_TYPE_ID = documentTypeIds.legal.identificationCard

  // Determina si el campo de Número de Aviso Operacional debe mostrarse (solo para RUC y Cédula)
  const currentDocumentTypeId =
    formikLegal.values.documentTypeId?.toUpperCase() || ''
  const showOperationalNoticeField =
    currentDocumentTypeId === RUC_DOCUMENT_TYPE_ID.toUpperCase() ||
    currentDocumentTypeId === CEDULA_DOCUMENT_TYPE_ID.toUpperCase()

  // Función para verificar RUC duplicado (usada en onBlur y onSubmit)
  const checkDuplicateRuc = useCallback(
    async (showModal = true) => {
      const rucValue = formikLegal.values.ruc?.trim()
      const documentTypeId = formikLegal.values.documentTypeId?.toUpperCase()

      // Solo validar si el tipo de documento es RUC o Cédula
      const isRuc = documentTypeId === RUC_DOCUMENT_TYPE_ID.toUpperCase()
      const isCedula = documentTypeId === CEDULA_DOCUMENT_TYPE_ID.toUpperCase()
      if (!isRuc && !isCedula) {
        setHasDuplicateRuc(false)
        return { hasDuplicate: false, clients: [] }
      }

      // Si el RUC está vacío o es muy corto, no buscar
      if (!rucValue || rucValue.length < 3) {
        setHasDuplicateRuc(false)
        return { hasDuplicate: false, clients: [] }
      }

      // Si ya se verificó este RUC, retornar el estado actual
      if (lastCheckedRuc.current === rucValue) {
        return { hasDuplicate: hasDuplicateRuc, clients: duplicateClients }
      }

      setIsCheckingRuc(true)

      try {
        const payload = {
          companyId,
          GlobalExecution: true,
          SearchText: rucValue,
          RoleName: 'Customer',
          PageSize: 10,
          pageNumber: 1,
          CustomerTypeId: customerTypeId.legal,
        }
        const customers = await dispatch(fetchSearchCustomers(payload))

        setIsCheckingRuc(false)
        lastCheckedRuc.current = rucValue

        // Si hay clientes, filtrar los que coinciden exactamente con el RUC y excluir el cliente que se está editando
        if (customers && customers?.length > 0) {
          // Filtrar solo clientes con RUC exactamente igual al ingresado y de tipo Empresa
          const exactMatchCustomers = customers.filter(
            customer =>
              customer.NumberId?.trim()?.toUpperCase() ===
              rucValue.toUpperCase()
          )

          // Filtrar el cliente que se está editando de la lista
          const filteredCustomers = clientSelected?.edit
            ? exactMatchCustomers.filter(
                customer => customer.CustomerId !== clientSelected.customerID
              )
            : exactMatchCustomers

          // Solo mostrar el modal si hay clientes duplicados después de filtrar
          if (filteredCustomers.length > 0) {
            setHasDuplicateRuc(true)
            if (showModal) {
              handleOpenModal(filteredCustomers)
            }
            return { hasDuplicate: true, clients: filteredCustomers }
          } else {
            setHasDuplicateRuc(false)
            return { hasDuplicate: false, clients: [] }
          }
        } else {
          setHasDuplicateRuc(false)
          return { hasDuplicate: false, clients: [] }
        }
      } catch (error) {
        console.error('Error al buscar clientes por RUC:', error)
        setIsCheckingRuc(false)
        setHasDuplicateRuc(false)
        return { hasDuplicate: false, clients: [] }
      }
    },
    [
      formikLegal.values.ruc,
      formikLegal.values.documentTypeId,
      handleOpenModal,
      dispatch,
      companyId,
      clientSelected,
      hasDuplicateRuc,
      duplicateClients,
      RUC_DOCUMENT_TYPE_ID,
      CEDULA_DOCUMENT_TYPE_ID,
    ]
  )

  // Handler para onBlur del campo RUC
  const handleRucBlur = useCallback(
    async e => {
      formikLegal.handleBlur(e)
      await checkDuplicateRuc(true)
    },
    [formikLegal, checkDuplicateRuc]
  )

  // Resetear lastCheckedRuc cuando cambia el valor del RUC
  useEffect(() => {
    const rucValue = formikLegal.values.ruc?.trim()
    if (lastCheckedRuc.current !== rucValue) {
      lastCheckedRuc.current = null
      setHasDuplicateRuc(false)
    }
  }, [formikLegal.values.ruc])

  // Disparar validación de duplicado cuando se cambia el tipo de documento a RUC o Cédula
  // y ya existe un valor ingresado en el campo de identificación
  useEffect(() => {
    // Ignorar la ejecución inicial del montaje del componente
    if (isFirstDocTypeRender.current) {
      isFirstDocTypeRender.current = false
      return
    }

    const rucValue = formikLegal.values.ruc?.trim()
    const docType = formikLegal.values.documentTypeId?.toUpperCase()
    const isRucOrCedula =
      docType === RUC_DOCUMENT_TYPE_ID.toUpperCase() ||
      docType === CEDULA_DOCUMENT_TYPE_ID.toUpperCase()

    if (isRucOrCedula && rucValue && rucValue.length >= 5) {
      lastCheckedRuc.current = null // Forzar re-verificación ignorando caché
      checkDuplicateRuc(true)
    } else {
      setHasDuplicateRuc(false)
    }
  }, [formikLegal.values.documentTypeId])

  // Handler para validar antes de guardar
  const handleSubmitWithValidation = async () => {
    // Si el tipo de documento no es RUC ni Cédula, limpiar el OperationNoticeNumber antes de guardar
    if (!showOperationalNoticeField) {
      formikLegal.setFieldValue('OperationNoticeNumber', '')
    }

    // Verificar RUC duplicado antes de submit (sin mostrar modal)
    const { hasDuplicate } = await checkDuplicateRuc(false)

    const operationNoticeNumber =
      formikLegal.values.OperationNoticeNumber?.trim()

    // Verificar si OperationNoticeNumber es requerido pero está vacío
    const isOperationNoticeRequired = hasDuplicate && !operationNoticeNumber
    const isOperationNoticeTooShort =
      hasDuplicate &&
      operationNoticeNumber?.length > 0 &&
      operationNoticeNumber.length < 5

    // Validar todos los campos primero
    const errors = await formikLegal.validateForm()
    const hasFormikErrors = Object.keys(errors).length > 0

    // Si el OperationNoticeNumber es requerido pero está vacío, mostrar error y NO guardar
    if (isOperationNoticeRequired) {
      formikLegal.setFieldTouched('OperationNoticeNumber', true, false)
      formikLegal.setFieldError(
        'OperationNoticeNumber',
        t('CREATE_CLIENT_T.OPERATIONAL_NOTICE_NUMBER_REQUIRED')
      )
      return
    }
    if (isOperationNoticeTooShort) {
      formikLegal.setFieldTouched('OperationNoticeNumber', true, false)
      formikLegal.setFieldError(
        'OperationNoticeNumber',
        t('CREATE_CLIENT_T.OPERATIONAL_NOTICE_NUMBER_MIN_LENGTH')
      )
      return
    }

    // Si solo hay errores de Formik (sin problema de OperationNoticeNumber)
    if (hasFormikErrors) {
      formikLegal.handleSubmit()
      return
    }

    // Si no hay errores, continuar con el submit normal
    formikLegal.handleSubmit()
  }

  return (
    <div className="form-area">
      <form>
        <Row>
          <Row>
            {/* LEGAL NAME */}
            <Col xl={6} lg={6} md={12} sm={12}>
              <div className="form-group">
                <label htmlFor="legalName">
                  {t('CREATE_CLIENT_T.COMPANY_NAME')}
                </label>
                <input
                  type="text"
                  id="legalName"
                  name="legalName"
                  data-testid="input-company-name"
                  className={
                    formikLegal.touched.legalName &&
                    formikLegal.errors.legalName
                      ? 'form-control error'
                      : 'form-control'
                  }
                  placeholder={t('CREATE_CLIENT_T.COMPANY_NAME')}
                  value={formikLegal.values.legalName}
                  onChange={e => {
                    const value = e.target.value
                    formikLegal.setFieldValue('legalName', value.toUpperCase())
                  }}
                  onBlur={formikLegal.handleBlur}
                />
              </div>
              <div className="input-feedback">
                {formikLegal.touched.legalName &&
                  formikLegal.errors.legalName && (
                    <strong>
                      {t('CREATE_CLIENT_T.INTRODUCE_VALID_COMPANY_NAME')}{' '}
                    </strong>
                  )}
              </div>
            </Col>
            {/* COMERCIAL NAME */}
            <Col xl={6} lg={6} md={12} sm={12}>
              <div className="form-group">
                <label htmlFor="comercialName">
                  {t('CREATE_CLIENT_T.COMPANY_COMMERCIAL_NAME')}
                </label>
                <input
                  type="text"
                  id="comercialName"
                  name="comercialName"
                  className={
                    formikLegal.touched.comercialName &&
                    formikLegal.errors.comercialName
                      ? 'form-control error'
                      : 'form-control'
                  }
                  placeholder={t('CREATE_CLIENT_T.COMPANY_COMMERCIAL_NAME')}
                  value={formikLegal.values.comercialName}
                  onChange={e => {
                    const value = e.target.value
                    formikLegal.setFieldValue(
                      'comercialName',
                      value.toUpperCase()
                    )
                  }}
                  onBlur={formikLegal.handleBlur}
                />
              </div>
              <div className="input-feedback">
                {formikLegal.touched.comercialName &&
                  formikLegal.errors.comercialName && (
                    <strong>
                      {t('CREATE_CLIENT_T.INVALID_COMPANY_NAME')}{' '}
                    </strong>
                  )}
              </div>
            </Col>
          </Row>
          <Row>
            <Col xl={6} sm={12}>
              <Row className="mx-0">
                {/* TYPE DOCUMENT COMERCIAL */}
                <Col xl={12} lg={12} md={12} sm={12}>
                  <div className="form-group radio-group">
                    <div className="d-flex flex-row">
                      {documentType.map((tipo, index) => (
                        <div
                          className="custom-control custom-radio mr-2"
                          key={index}>
                          <input
                            type="radio"
                            id={`radiodocumento${index}`}
                            className="custom-control-input"
                            name="documentTypeId"
                            data-testid={`radio-document-${tipo.DocumentTypeId}`}
                            value={tipo.DocumentTypeId.toUpperCase()}
                            checked={
                              formikLegal.values?.documentTypeId.toUpperCase() ===
                              tipo.DocumentTypeId.toUpperCase()
                            }
                            onChange={formikLegal.handleChange}
                            onBlur={formikLegal.handleBlur}
                          />
                          <label
                            className="custom-control-label label-radio"
                            htmlFor={`radiodocumento${index}`}
                            style={{
                              marginRight: '15px',
                            }}>
                            {tipo.Name}
                          </label>
                        </div>
                      ))}
                      <DocumentTypeTooltipLegal
                        style={{ marginLeft: 0, marginTop: 4 }}
                      />
                    </div>
                  </div>
                  {formikLegal.touched.documentTypeId &&
                    formikLegal.errors.documentTypeId && (
                      <div className="input-feedback">
                        <strong>{formikLegal.errors.documentTypeId}</strong>
                      </div>
                    )}
                </Col>
                {/* RUC */}
                <Col xl={10} lg={10} md={6} sm={12}>
                  <div className="form-group" style={{ position: 'relative' }}>
                    <input
                      type="text"
                      name="ruc"
                      maxLength={20}
                      data-testid="input-ruc"
                      className={
                        formikLegal.touched.ruc && formikLegal.errors.ruc
                          ? 'form-control error'
                          : 'form-control'
                      }
                      placeholder={t(
                        'CREATE_CLIENT_T.INTRODUCE_IDENT_PLACEHOLDER'
                      )}
                      value={formikLegal.values.ruc}
                      onChange={e => {
                        const value = e.target.value
                        formikLegal.setFieldValue('ruc', value.toUpperCase())
                      }}
                      onBlur={handleRucBlur}
                    />
                    {isCheckingRuc && (
                      <div
                        style={{
                          position: 'absolute',
                          right: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                        }}>
                        <CircularProgress size={18} />
                      </div>
                    )}
                  </div>
                  <div className="input-feedback">
                    {formikLegal.touched.ruc && formikLegal.errors.ruc && (
                      <strong>{formikLegal.errors.ruc}</strong>
                    )}
                  </div>
                </Col>
                {/* DV */}
                <Col xl={2} lg={2} md={6} sm={12} className="px-0">
                  <div className="form-group">
                    <input
                      type="text"
                      name="dv"
                      maxLength={2}
                      data-testid="input-dv"
                      className={
                        formikLegal.touched.dv && formikLegal.errors.dv
                          ? 'form-control error'
                          : 'form-control'
                      }
                      placeholder="DV"
                      value={formikLegal.values.dv}
                      onChange={e => {
                        const onlyNums = e.target.value.replace(/[^0-9]/g, '')
                        formikLegal.setFieldValue('dv', onlyNums)
                      }}
                      onBlur={formikLegal.handleBlur}
                    />
                  </div>
                  <div className="input-feedback">
                    {formikLegal.touched.dv && formikLegal.errors.dv && (
                      <strong>{t('CREATE_CLIENT_T.INVALID_DV')}</strong>
                    )}
                  </div>
                </Col>
              </Row>
            </Col>
            {/* Número de Aviso Operacional - solo para RUC o Cédula */}
            {showOperationalNoticeField && (
              <Col xl={6} lg={6} md={12} sm={12}>
                <div className="form-group">
                  <label htmlFor="OperationNoticeNumber">
                    {t('CREATE_CLIENT_T.OPERATIONAL_NOTICE_NUMBER') +
                      (hasDuplicateRuc ? '*' : '')}
                  </label>
                  <input
                    type="text"
                    id="OperationNoticeNumber"
                    name="OperationNoticeNumber"
                    className={
                      formikLegal.touched.OperationNoticeNumber &&
                      formikLegal.errors.OperationNoticeNumber
                        ? 'form-control error'
                        : 'form-control'
                    }
                    placeholder={t('CREATE_CLIENT_T.OPERATIONAL_NOTICE_NUMBER')}
                    value={formikLegal.values.OperationNoticeNumber}
                    onChange={e => {
                      formikLegal.setFieldValue(
                        'OperationNoticeNumber',
                        e.target.value
                      )
                    }}
                    onBlur={formikLegal.handleBlur}
                  />
                </div>
                <div className="input-feedback">
                  {formikLegal.touched.OperationNoticeNumber &&
                    formikLegal.errors.OperationNoticeNumber && (
                      <strong>
                        {formikLegal.errors.OperationNoticeNumber}
                      </strong>
                    )}
                </div>
              </Col>
            )}
            <Col xl={6} sm={12}>
              {/* TIPOCLIENTE */}
              {canUseCustomerType && (
                <DisplayCustomerGroup
                  formik={formikLegal}
                  customerTypeId={customerTypeId.legal}
                  customerType="legal"
                />
              )}
            </Col>
          </Row>
          <Row>
            {/* regexEmail */}
            <Col xl={6} lg={6} md={6} sm={12}>
              <div className="form-group" style={{ marginTop: '4px' }}>
                <div>
                  <label htmlFor="email">
                    {t('CREATE_CLIENT_T.EMAIL_PLACEHOLDER')}
                  </label>
                  <EmailTooltip />
                </div>
                <input
                  id="email"
                  type="email"
                  name="generalStore[0].generalEmail[0].address"
                  data-testid="input-email-company"
                  className={
                    formikLegal.touched.hasOwnProperty('generalStore') &&
                    formikLegal.errors.hasOwnProperty('generalEmail')
                      ? formikLegal.errors.generalEmail[0].address
                        ? 'form-control error'
                        : 'form-control'
                      : 'form-control'
                  }
                  placeholder={t('CREATE_CLIENT_T.EMAIL_PLACEHOLDER')}
                  value={
                    formikLegal.values?.generalStore[0]?.generalEmail[0]
                      ?.address
                  }
                  onChange={formikLegal.handleChange}
                  onBlur={formikLegal.handleBlur}
                />
              </div>
              <div className="input-feedback">
                {formikLegal.touched.hasOwnProperty('generalStore') &&
                  formikLegal.errors.hasOwnProperty('generalEmail') && (
                    <strong>
                      {formikLegal.errors.generalEmail[0].address}
                    </strong>
                  )}
              </div>
            </Col>
            {/* TYPE PHONE NUMBER */}
            <Col xl={6} lg={6} md={6} sm={12}>
              <div className="form-group radio-group">
                <div className="d-flex flex-row">
                  {tipoTelefono.map((tipo, index) => (
                    <div
                      className="custom-control custom-radio mr-2"
                      key={index}>
                      <input
                        type="radio"
                        id={`radiotipotelefonoJuridico${index}`}
                        className="custom-control-input"
                        name="generalStore[0].generalTelephone[0].telephoneTypeId"
                        value={tipo.TelephoneTypeId.toUpperCase()}
                        data-testid={`radio-telephone-type-${tipo.TelephoneTypeId}`}
                        checked={
                          formikLegal.values?.generalStore[0]?.generalTelephone[0]?.telephoneTypeId?.toUpperCase() ===
                          tipo?.TelephoneTypeId?.toUpperCase()
                        }
                        onChange={formikLegal.handleChange}
                      />
                      <label
                        className="custom-control-label label-radio"
                        htmlFor={`radiotipotelefonoJuridico${index}`}
                        style={{
                          marginRight: '15px',
                        }}>
                        {tipo.Description}
                      </label>
                    </div>
                  ))}
                  <PhoneTypeTooltip style={{ marginLeft: 0, marginTop: 4 }} />
                </div>
              </div>
              {formikLegal.touched?.generalStore &&
                formikLegal.touched?.generalStore[0]?.generalTelephone &&
                formikLegal.touched?.generalStore[0]?.generalTelephone[0] &&
                formikLegal.errors?.telephoneTypeId && (
                  <div className="input-feedback">
                    <strong>{formikLegal.errors?.telephoneTypeId}</strong>
                  </div>
                )}
              {/* PHONENUMBER */}
              <div>
                <div className="form-group">
                  <PhoneNumberField
                    formik={formikLegal}
                    fieldName={'generalStore[0].generalTelephone[0]'}
                    fieldToCheck={'generalStore'}
                  />
                </div>
                <div className="input-feedback">
                  {formikLegal.touched.hasOwnProperty('generalStore') &&
                    formikLegal.errors.hasOwnProperty('generalTelephone') && (
                      <strong>
                        {formikLegal.errors?.generalTelephone[0]?.number}
                      </strong>
                    )}
                </div>
              </div>
            </Col>
          </Row>

          {/* LISTAS */}
          {canUserAssignPriceLists && (
            <Row>
              <Col lg={6} sm={12}>
                <PriceList
                  priceListSelected={valueState}
                  setPriceListSelected={setValueState}
                />
              </Col>
            </Row>
          )}
          {/* MAPA DIRECCIÓN */}
          {/* DEBE MANTENERSE DE ESTA FORMA O SE PUEDE GENERAR UN ERROR EN CASO DE QUE OCURRA UNA RENDERIZACIÓN */}
          {!clientSelected?.edit && (
            <Col lg={12}>
              <NewCustomerMap
                setHasAddress={setHasAddress}
                hasAddress={hasAddress}
                setCountryCode={setCountryCode}
                setProvinceCode={setProvinceCode}
                setDistrictCode={setDistrictCode}
                setTownshipCode={setTownshipCode}
                personType={'JUR'}
                street={street}
                setStreet={setStreet}
                name={name}
                setName={setName}
                formik={formikLegal}
              />
            </Col>
          )}
          {/* RECIBIR PROMOCIONES */}
          <Col lg={12}>
            <div className="custom-control d-inline-block mr-2">
              <input
                type="checkbox"
                className="custom-control-input checkbox-input"
                name="receivePromotions"
                id="multicheckbox1"
                value={formikLegal.values.receivePromotions}
                checked={formikLegal.values.receivePromotions}
                onChange={formikLegal.handleChange}
              />
              <label className="custom-control-label" htmlFor="multicheckbox1">
                {t('CREATE_CLIENT_T.ADVERTISE_EMAIL_INFO')}{' '}
              </label>
            </div>
          </Col>
          <div className="section-buttons">
            <button
              disabled={loadingSave}
              type="button"
              onClick={handleSubmitWithValidation}
              data-testid="button-save-client"
              className="btn btn-primary form-submit buttons-save">
              {loadingSave ? (
                <CircularProgress size="2.5rem" />
              ) : !clientSelected?.edit ? (
                t('CREATE_CLIENT_T.CREATE_CLIENT_SAVE_BUTTON')
              ) : (
                t('CREATE_CLIENT_T.SAVE_BUTTON')
              )}
            </button>
          </div>
        </Row>
      </form>

      {/* Modal de RUC duplicado */}
      <DuplicateRucModal
        open={isModalOpen}
        onClose={handleCloseModal}
        duplicateClients={duplicateClients}
        onContinue={handleContinueModal}
        isLoading={false}
        editingCustomerId={
          clientSelected?.edit ? clientSelected.customerID : null
        }
        initialOperationalNumber={
          clientSelected?.edit ? formikLegal.values.OperationNoticeNumber : ''
        }
        formRuc={formikLegal.values.ruc}
      />
    </div>
  )
}
